import moment from 'moment';
import { store } from '../index';
import { setFilterDateRange as setHospitalDateFilter, setLoading as setHospitalLoading } from '../store/reducers/hospital.slice';
import { setFilterDateRange as setCommunityDateFilter, setLoading as setCommunityLoading } from '../store/reducers/community-transfer.slice';
import { setFilterDateRange as setAdmissionDateFilter, setLoading as setAdmissionLoading } from '../store/reducers/admission.slice';
import { setFilterDateRange as setDescentDateFilter, setLoading as setDescentLoading } from '../store/reducers/deceased.slice';
import { setFilterDateRange as setOverallDateFilter, setLoading as setOverallLoading } from '../store/reducers/overall.slice';
import { setFilterDateRange as setQuickGlaceDateFilter } from '../store/reducers/quick-glace.slice';
import { setFilterDateUpdate } from '../store/reducers/common.slice';
import { setIsOverLapDateMinimum, setIsOverLapComparison, setIsComparisonRangeSet } from '../store/reducers/comparisonReducers/comparison.slice';

/**
 * AI Date Actions Service
 * Handles actual date changes through DateFilterComponent integration
 * This service can trigger real date filter updates, not just suggestions
 */
class AiDateActionsService {
    constructor() {
        this.validationContext = null;
        this.updateDashboard = null;
        this.handleFilterUpdate = null;
        this.dateFilterHandler = null; // Store the actual DateFilterComponent.handleOnChange
    }

    /**
     * Initialize the service with required callbacks
     * @param {Object} validationContext - Validation context (census dates, pause dates, etc.)
     * @param {Function} updateDashboard - Dashboard context update function
     * @param {Function} handleFilterUpdate - Filter update callback that triggers API refresh
     */
    initialize(validationContext = {}, updateDashboard = null, handleFilterUpdate = null) {
        this.validationContext = validationContext;
        this.updateDashboard = updateDashboard;
        this.handleFilterUpdate = handleFilterUpdate;
        console.log('🔧 AI Date Actions Service initialized:', {
            hasUpdateDashboard: !!updateDashboard,
            hasFilterUpdate: !!handleFilterUpdate
        });
    }

    /**
     * Set the DateFilterComponent's handleOnChange function
     * @param {Function} handler - The handleOnChange function from DateFilterComponent
     */
    setDateFilterHandler(handler) {
        this.dateFilterHandler = handler;
        // console.log('🎯 DateFilterComponent handler set:', !!handler);
    }

    /**
     * Get the DateFilterComponent's handleOnChange function
     * @returns {Function|null} The handler function
     */
    getDateFilterHandler() {
        return this.dateFilterHandler;
    }

    /**
     * Update validation context
     * @param {Object} context - Updated validation context
     */
    updateValidationContext(context) {
        this.validationContext = { ...this.validationContext, ...context };
    }

    /**
     * Apply a date range following the exact same flow as DateFilterComponent.handleOnChange
     * @param {string} startDate - Start date (YYYY-MM-DD format)
     * @param {string} endDate - End date (YYYY-MM-DD format)
     * @param {string} label - Human-readable label for the range
     * @returns {Promise<Object>} Result of the date change
     */
    async applyDateRange(startDate, endDate, label = '') {
        try {
            // Convert string dates to Date objects (same format as DateFilterComponent expects)
            const startDateObj = moment(startDate).startOf('day').toDate();
            const endDateObj = moment(endDate).startOf('day').toDate();

            // Create the exact same format as DateFilterComponent receives from date picker
            const selection = [{
                startDate: startDateObj,
                endDate: endDateObj,
                key: "selection"
            }];

            const value = selection[0];
            
            console.log('🔄 AI Date Actions - Applying date range (DateFilterComponent flow):', {
                label,
                startDate: startDate,
                endDate: endDate,
                selection,
                value
            });

            // Step 1: Set loading states (like DateFilterComponent line 356)
            this.updateLoadingToAllDashboard(true);

            // Step 2: Set comparison states (like DateFilterComponent line 342)
            store.dispatch(setIsComparisonRangeSet(true));
            store.dispatch(setFilterDateUpdate(false));

            // Step 3: Create the filter object with proper date formatting (like DateFilterComponent line 360-364)
            const latestValue = {
                ...value,
                startDate: moment(value.startDate).startOf("day").toDate(),
                endDate: moment(value.endDate).startOf("day").toDate(),
            };

            // Step 4: Update dashboard context (like DateFilterComponent line 374)
            if (this.updateDashboard) {
                this.updateDashboard({ filter: latestValue });
                console.log('📊 Updated dashboard context with new filter');
            }

            // Step 5: Update all dashboard date filters (like DateFilterComponent line 375 & 368)
            await this.updateDateToAllDashboard(latestValue);

            // Step 6: Trigger API refresh (like DateFilterComponent line 370)
            if (this.handleFilterUpdate) {
                this.handleFilterUpdate(true);
                console.log('🔄 Triggered API refresh');
            }

            // Step 7: Clean up comparison states (like DateFilterComponent line 378-379)
            store.dispatch(setIsOverLapDateMinimum(false));
            store.dispatch(setIsOverLapComparison(false));

            console.log('✅ AI Date Actions - Successfully applied date range following DateFilterComponent flow');

            return {
                success: true,
                message: `✅ Successfully applied date range: ${label}`,
                appliedRange: {
                    label,
                    startDate,
                    endDate
                },
                actionPerformed: true
            };

        } catch (error) {
            console.error('❌ AI Date Actions - Error applying date range:', error);
            return {
                success: false,
                error: `Failed to apply date range: ${error.message}`,
                suggestion: `Try manually selecting the date range: ${startDate} to ${endDate}`
            };
        }
    }

    /**
     * Update loading states for all dashboards (replicates DateFilterComponent.updateLoadingToAllDashboard)
     * @param {boolean} value - Loading state
     */
    updateLoadingToAllDashboard(value) {
        store.dispatch(setHospitalLoading(value));
        store.dispatch(setOverallLoading(value));
        store.dispatch(setDescentLoading(value));
        store.dispatch(setCommunityLoading(value));
        store.dispatch(setAdmissionLoading(value));
    }

    /**
     * Update date filters for all dashboards (replicates DateFilterComponent.updateDateToAllDashboard)
     * @param {Object} value - Date filter value
     */
    async updateDateToAllDashboard(value) {
        store.dispatch(setHospitalDateFilter(value));
        store.dispatch(setCommunityDateFilter(value));
        store.dispatch(setAdmissionDateFilter(value));
        store.dispatch(setDescentDateFilter(value));
        store.dispatch(setOverallDateFilter(value));
        store.dispatch(setQuickGlaceDateFilter(value));
    }

    /**
     * Apply "Last 7 Days" date range
     * @returns {Promise<Object>} Result of the date change
     */
    async applyLast7Days() {
        const endDate = moment().format('YYYY-MM-DD');
        const startDate = moment().subtract(6, 'days').format('YYYY-MM-DD');
        return this.applyDateRange(startDate, endDate, 'Last 7 Days');
    }

    /**
     * Apply "Last 30 Days" date range
     * @returns {Promise<Object>} Result of the date change
     */
    async applyLast30Days() {
        const endDate = moment().format('YYYY-MM-DD');
        const startDate = moment().subtract(29, 'days').format('YYYY-MM-DD');
        return this.applyDateRange(startDate, endDate, 'Last 30 Days');
    }

    /**
     * Apply "This Week" date range
     * @returns {Promise<Object>} Result of the date change
     */
    async applyThisWeek() {
        const startDate = moment().startOf('week').format('YYYY-MM-DD');
        const endDate = moment().format('YYYY-MM-DD');
        return this.applyDateRange(startDate, endDate, 'This Week');
    }

    /**
     * Apply "This Month" date range
     * @returns {Promise<Object>} Result of the date change
     */
    async applyThisMonth() {
        const startDate = moment().startOf('month').format('YYYY-MM-DD');
        const endDate = moment().format('YYYY-MM-DD');
        return this.applyDateRange(startDate, endDate, 'This Month');
    }

    /**
     * Apply "Yesterday" date range
     * @returns {Promise<Object>} Result of the date change
     */
    async applyYesterday() {
        const date = moment().subtract(1, 'day').format('YYYY-MM-DD');
        return this.applyDateRange(date, date, 'Yesterday');
    }

    /**
     * Apply "Today" date range
     * @returns {Promise<Object>} Result of the date change
     */
    async applyToday() {
        const date = moment().format('YYYY-MM-DD');
        return this.applyDateRange(date, date, 'Today');
    }

    /**
     * Apply custom day range (e.g., last 14 days, last 45 days)
     * @param {number} days - Number of days
     * @returns {Promise<Object>} Result of the date change
     */
    async applyCustomDayRange(days) {
        const endDate = moment().format('YYYY-MM-DD');
        const startDate = moment().subtract(days - 1, 'days').format('YYYY-MM-DD');
        return this.applyDateRange(startDate, endDate, `Last ${days} Days`);
    }

    /**
     * Apply specific month range
     * @param {string} month - Month name (e.g., 'december', 'january')
     * @param {number} year - Year (optional, defaults to appropriate year)
     * @returns {Promise<Object>} Result of the date change
     */
    async applyMonthRange(month, year = null) {
        const monthIndex = [
            'january', 'february', 'march', 'april', 'may', 'june',
            'july', 'august', 'september', 'october', 'november', 'december'
        ].indexOf(month.toLowerCase());

        if (monthIndex === -1) {
            return {
                success: false,
                error: `Invalid month: ${month}`
            };
        }

        const currentYear = moment().year();
        const targetYear = year || currentYear;
        
        const monthStart = moment().year(targetYear).month(monthIndex).startOf('month');
        const monthEnd = moment().year(targetYear).month(monthIndex).endOf('month');
        
        // If it's a future month in current year, use previous year
        if (targetYear === currentYear && monthStart.isAfter(moment())) {
            monthStart.subtract(1, 'year');
            monthEnd.subtract(1, 'year');
        }

        const startDate = monthStart.format('YYYY-MM-DD');
        const endDate = monthEnd.format('YYYY-MM-DD');
        const label = `${month.charAt(0).toUpperCase() + month.slice(1)} ${monthStart.year()}`;

        return this.applyDateRange(startDate, endDate, label);
    }

    /**
     * Check if the service is properly initialized
     * @returns {boolean} True if service can execute date changes
     */
    isInitialized() {
        return !!this.dateChangeHandler;
    }

    /**
     * Get validation info for debugging
     * @returns {Object} Current validation context
     */
    getValidationInfo() {
        return {
            hasHandler: !!this.dateChangeHandler,
            validationContext: this.validationContext
        };
    }
}

// Create singleton instance
const aiDateActionsService = new AiDateActionsService();

export default aiDateActionsService;
